import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Activity, TrendingUp, AlertTriangle, CheckCircle, RefreshCw, IndianRupee, Users, Calendar, Package } from 'lucide-react';
import { theme, cardStyle } from '../theme';

export default function HealthScorePage({ API_BASE }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (fresh = false) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/health-score${fresh ? '?fresh=1' : ''}`);
      setData(r.data);
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setLoading(false);
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>Loading clinic vitals...</div>;
  if (!data) return null;

  const scoreColor = data.score >= 85 ? theme.success : data.score >= 70 ? '#65a30d' : data.score >= 55 ? theme.warning : data.score >= 40 ? '#f97316' : theme.danger;
  const m = data.metrics;

  return (
    <div style={{ padding: 20, maxWidth: 1280, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
        <h2 style={{ margin: 0, color: theme.navy }}>📊 Clinic Health Score</h2>
        <button onClick={()=>load(true)} style={btn(theme.primary)}><RefreshCw size={14}/> Recompute</button>
      </div>

      {/* Hero score card */}
      <div style={{ ...cardStyle, padding: 28, marginBottom: 18, background: `linear-gradient(135deg, ${scoreColor}15, ${scoreColor}05)`, borderLeft: `6px solid ${scoreColor}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          <div style={{
            width: 140, height: 140, borderRadius: '50%',
            background: `conic-gradient(${scoreColor} ${data.score * 3.6}deg, #e5e9f0 ${data.score * 3.6}deg)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <div style={{ width: 116, height: 116, borderRadius: '50%', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ fontSize: 38, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{data.score}</div>
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>out of 100</div>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 13, color: theme.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Clinic Grade</div>
            <div style={{ fontSize: 48, fontWeight: 800, color: scoreColor, lineHeight: 1, marginTop: 4 }}>{data.grade} <span style={{ fontSize: 22, color: theme.navy }}>· {data.label}</span></div>
            <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 8 }}>
              Updated {new Date(data.asOf).toLocaleString('en-IN')}
              {data.cached && <span style={{ marginLeft: 8, padding: '2px 8px', background: theme.borderLight, borderRadius: 8, fontSize: 10 }}>cached</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Sub-scores */}
      <h3 style={{ margin: '0 0 10px', color: theme.navy }}>Score Breakdown</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
        <SubScore label="Collection" value={data.subscores.collection}/>
        <SubScore label="No-Show Health" value={data.subscores.noShow}/>
        <SubScore label="Patient Acquisition" value={data.subscores.acquisition}/>
        <SubScore label="Case Acceptance" value={data.subscores.caseAcceptance}/>
        <SubScore label="AR Ageing" value={data.subscores.ar}/>
        <SubScore label="Operations" value={data.subscores.operations}/>
      </div>

      {/* KPI grid */}
      <h3 style={{ margin: '0 0 10px', color: theme.navy }}>Key Metrics</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 18 }}>
        <Stat icon={Users}    label="Total Patients" value={m.patients.total} sub={`+${m.patients.new30Days} in last 30d (${m.patients.growthPct >= 0 ? '+' : ''}${m.patients.growthPct}%)`} color={theme.info}/>
        <Stat icon={IndianRupee} label="This Month Production" value={`₹${(m.production.month||0).toLocaleString('en-IN')}`} sub={`Today: ₹${(m.production.today||0).toLocaleString('en-IN')}`} color={theme.success}/>
        <Stat icon={IndianRupee} label="Collected (month)" value={`₹${(m.collection.month||0).toLocaleString('en-IN')}`} sub={`Rate: ${m.collection.rate}%`} color={theme.success}/>
        <Stat icon={IndianRupee} label="Outstanding (AR)" value={`₹${(m.ar.totalOutstanding||0).toLocaleString('en-IN')}`} sub={`${m.ar.days} days · ${m.ar.unpaidBillCount} bills`} color={m.ar.days > 30 ? theme.danger : theme.warning}/>
        <Stat icon={Calendar} label="Appointments (month)" value={m.appointments.monthScheduled} sub={`${m.appointments.monthCompleted} done · ${m.appointments.monthNoShow} no-show (${m.appointments.noShowPct}%)`} color={theme.purple}/>
        <Stat icon={CheckCircle} label="Case Acceptance" value={`${m.treatments.caseAcceptance}%`} sub={`${m.treatments.done} done / ${m.treatments.planned} planned`} color={theme.secondary}/>
        <Stat icon={Package}  label="Inventory Risk" value={`${m.inventory.lowStock + m.inventory.expiringMeds}`} sub={`${m.inventory.lowStock} low · ${m.inventory.expiringMeds} expiring`} color={(m.inventory.lowStock + m.inventory.expiringMeds) > 0 ? theme.warning : theme.success}/>
        <Stat icon={Activity} label="Recall Backlog" value={m.recalls.overdue} sub={`Overdue (out of ${m.recalls.total} pending)`} color={m.recalls.overdue > 5 ? theme.warning : theme.success}/>
      </div>

      {/* Risks */}
      {data.risks.length > 0 && (
        <>
          <h3 style={{ margin: '0 0 10px', color: theme.navy }}>⚠️ Action Items</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
            {data.risks.map((r, i) => (
              <div key={i} style={{ ...cardStyle, padding: 12, borderLeft: `4px solid ${r.severity === 'high' ? theme.danger : r.severity === 'medium' ? theme.warning : theme.info}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                <AlertTriangle size={16} color={r.severity === 'high' ? theme.danger : r.severity === 'medium' ? theme.warning : theme.info}/>
                <div style={{ flex: 1, fontSize: 13.5, color: theme.navy }}>{r.text}</div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: r.severity === 'high' ? '#fee2e2' : r.severity === 'medium' ? '#fef3c7' : '#dbeafe', color: r.severity === 'high' ? theme.danger : r.severity === 'medium' ? theme.warning : theme.info, textTransform: 'uppercase' }}>{r.severity}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {data.risks.length === 0 && (
        <div style={{ ...cardStyle, padding: 24, textAlign: 'center', background: theme.success + '10', border: `1px solid ${theme.success}40` }}>
          <CheckCircle size={40} color={theme.success}/>
          <div style={{ marginTop: 10, fontSize: 16, fontWeight: 700, color: theme.success }}>Sab kuch zabardast hai!</div>
          <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 4 }}>No critical issues detected.</div>
        </div>
      )}
    </div>
  );
}

const SubScore = ({ label, value }) => {
  const color = value >= 80 ? theme.success : value >= 60 ? theme.warning : theme.danger;
  return (
    <div style={{ ...cardStyle, padding: 14 }}>
      <div style={{ fontSize: 11, color: theme.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color, marginTop: 4 }}>{value}</div>
      <div style={{ height: 6, background: '#f1f3f5', borderRadius: 3, marginTop: 8, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${value}%`, background: color, borderRadius: 3 }}/>
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, sub, color }) => (
  <div style={{ ...cardStyle, padding: 14, borderLeft: `4px solid ${color}` }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Icon size={16} color={color}/>
      <div style={{ fontSize: 11, color: theme.textMuted, fontWeight: 700, textTransform: 'uppercase' }}>{label}</div>
    </div>
    <div style={{ fontSize: 22, fontWeight: 800, color: theme.navy, marginTop: 6 }}>{value}</div>
    <div style={{ fontSize: 11.5, color: theme.textMuted, marginTop: 2 }}>{sub}</div>
  </div>
);

const btn = (c) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: c, color: '#fff', border: 'none', borderRadius: theme.radiusSm, cursor: 'pointer', fontWeight: 600, fontSize: 13 });
